import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

export const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" aria-labelledby="hero-title">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" aria-hidden="true" />
      
      {/* Animated Glow Effects */}
      <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" aria-hidden="true" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} aria-hidden="true" />
      
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <article className="text-left space-y-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
              <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" aria-hidden="true" />
              <span className="text-sm font-medium text-muted-foreground">Free to use • No limits</span>
            </div>
            
            <div id="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              Chill, Study, Work:{" "}
              <h1 className="inline text-gradient">Your Personal Productivity Tool</h1>{" "}
              &amp; <h1 className="inline text-gradient">Online Workspace</h1>
            </div>
            
            <p className="text-lg sm:text-xl text-muted-foreground font-light max-w-xl leading-relaxed">
              CSW is more than just a study app – it's a community-powered productivity hub designed for students, professionals, and lifelong learners who thrive when they're connected. Whether you need to relax with friends, grind through an assignment, or plan a project, CSW's modes and tools adapt to your workflow and keep you accountable.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 w-full">
              <Button 
                size="lg" 
                className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg glow-primary transition-all hover:scale-105 w-full sm:w-auto"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Be Productive in 10 Seconds
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-border hover:bg-card font-semibold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg transition-all hover:scale-105 w-full sm:w-auto"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try our beta
                </a>
              </Button>
            </div>
            
            <aside className="flex flex-wrap items-center justify-center sm:justify-start gap-4 sm:gap-8 pt-8" aria-label="Key statistics">
              <div className="text-center sm:text-left">
                <p className="text-2xl sm:text-3xl font-bold text-gradient">10,000+</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Active Users</p>
              </div>
              <div className="w-px h-10 sm:h-12 bg-border hidden sm:block" aria-hidden="true" />
              <div className="text-center sm:text-left">
                <p className="text-2xl sm:text-3xl font-bold text-gradient">24/7</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Study Rooms</p>
              </div>
              <div className="w-px h-10 sm:h-12 bg-border hidden sm:block" aria-hidden="true" />
              <div className="text-center sm:text-left">
                <p className="text-2xl sm:text-3xl font-bold text-gradient">Free</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Forever</p>
              </div>
            </aside>
          </article>
          
          {/* Right Video */}
          <figure className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
              <video 
                src="/videos/productivity-demo.mp4" 
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-auto"
                aria-label="CSW productivity tool demo video showing the online workspace in action"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" aria-hidden="true" />
            </div>
            <figcaption className="sr-only">CSW online workspace demonstration - a productivity tool for focused studying and working</figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
};
